import * as _ from "lodash";
import {
    _makeJHHApiCall,
    _makeInSecureApiCall,
    IMakeJHHApiCall,
    IMakeInSecureApiCall,
    _getAccessToken,
    _setItemInLocalEncrypted,
    _getItemFromLocalDecrypted,
} from "network-helper";
import { notify } from "../components/Shared/Error/error";
import config from "../config.json";
import { EStatus } from "./Enums/generic";
import { inputParams } from "./GetCommonParam";
import { forceLogout } from "./Helpers/ForceLogout";
import { apiErrorIntentHanlder } from "./Helpers/JsBridgeHelper";
import { _setRequestToken, _getRefreshToken } from "./JHHToken";
import { _setItemInLocal } from "./LocalKeys";

const fallbackErrorMessage = "Something went wrong. Please try again.";

interface IFailedApiCallType {
    previousRest: IMakeJHHApiCall;
    isJHHTokenRequired: boolean;
    isDeviceDetailsRequired: boolean;
}

const callApiWithRefreshedToken = async (result: unknown, restDataObject: IFailedApiCallType) => {
    const status = _.get(result, "status", "");
    //@ts-ignore
    if (status === EStatus.SUCCESS) {
        requestTokenCalled = false;
        requestTokenRetry++;
        const accessToken = _getAccessToken();
        _.set(restDataObject.previousRest, "headers.token", accessToken);
        return await handleApiCall(
            restDataObject.previousRest,
            restDataObject.isJHHTokenRequired,
            restDataObject.isDeviceDetailsRequired
        );
    }
};

export const _setAccessTokenHeader = async () => {
    const result = await _setRequestToken({});
    const status = _.get(result, "status", "");
    const token = _.get(result, "token", "");
    //todo add an api resp enum
    if (status === "success") {
        return token;
    }
};

let requestTokenCalled = false;
let requestTokenRetry = 0;
let awaitTokenPromise: Promise<unknown>;

export const handleApiCall = async (
    { ...rest }: IMakeJHHApiCall,
    isJHHTokenRequired = false,
    isDeviceDetailsRequired = false,
    skipRequestToken = false
) => {
    try {
        if (isJHHTokenRequired === true) {
            const accessToken = _getAccessToken() === null ? await _setAccessTokenHeader() : _getAccessToken();
            _.set(rest, "headers.token", accessToken);
        }
        const EnvArray = [config.JHHMainEnv, config.JHHAppIntegrationService, config.JHHPatientAppEnv];
        let apimSubscriptionKey = config.ocp_token_core_platform;
        if (
            apimSubscriptionKey.length &&
            rest.url.length &&
            EnvArray.find((url) => rest.url.includes(url)) !== undefined
        ) {
            if (isDeviceDetailsRequired) {
                rest = {
                    ...rest,
                    dataObj: {
                        ...rest.dataObj,
                        ...inputParams(),
                    },
                    headers: {
                        ...rest.headers,
                        "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
                    },
                };
            } else {
                rest = {
                    ...rest,
                    headers: {
                        ...rest.headers,
                        "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
                    },
                };
            }
        }
        if (requestTokenCalled === true && skipRequestToken === false) {
            const result = await awaitTokenPromise;
            const restDataObject = {
                previousRest: rest,
                isJHHTokenRequired: isJHHTokenRequired,
                isDeviceDetailsRequired: isDeviceDetailsRequired,
            };
            return await callApiWithRefreshedToken(result, restDataObject);
        }
        const clonedRest = _.cloneDeep(rest);
        const jhhAPIResponse = await _makeJHHApiCall({ ...clonedRest });
        return jhhAPIResponse;
    } catch (err) {
        const statusCode = _.get(err, "response.status");
        const message = _.get(err, "message");
        const apiErrorMessage = _.get(err, "response.data.message", fallbackErrorMessage);
        if (statusCode === 401 && isJHHTokenRequired === true) {
            const restDataObject = {
                previousRest: rest,
                isJHHTokenRequired: isJHHTokenRequired,
                isDeviceDetailsRequired: isDeviceDetailsRequired,
            };
            if (requestTokenCalled === false && requestTokenRetry < 3) {
                awaitTokenPromise = _getRefreshToken();
                requestTokenCalled = true;
                const result = await awaitTokenPromise;
                return await callApiWithRefreshedToken(result, restDataObject);
            } else if (requestTokenCalled === true) {
                const result = await awaitTokenPromise;
                return await callApiWithRefreshedToken(result, restDataObject);
            } else if (requestTokenRetry >= 3) {
                let _hasBridgeHelper = await apiErrorIntentHanlder({
                    value: "request limit exceeded",
                    error_code: "401",
                });
                if (!_hasBridgeHelper) forceLogout();
            }
        } else if (statusCode === 412 && isJHHTokenRequired === true) {
            let _hasBridgeHelper = await apiErrorIntentHanlder({
                value: apiErrorMessage,
                error_code: "412",
            });
            if (!_hasBridgeHelper) forceLogout();
        } else if (statusCode === 410 && isJHHTokenRequired === true) {
            let _hasBridgeHelper = await apiErrorIntentHanlder({
                value: apiErrorMessage,
                error_code: "410",
            });
            if (!_hasBridgeHelper) {
                if (_getItemFromLocalDecrypted("switchToPrimaryProfile") !== "true") {
                    _setItemInLocalEncrypted("switchToPrimaryProfile", "true");
                    _setItemInLocal("_ne", apiErrorMessage);
                    window.location.href = `${config.relativePath}/`;
                }
            }
        } else if (statusCode === 403 && isJHHTokenRequired === true) {
            let _hasBridgeHelper = await apiErrorIntentHanlder({
                value: apiErrorMessage,
                error_code: "403",
            });
            if (!_hasBridgeHelper) forceLogout();
        } else if ((statusCode === 500 || statusCode === 504) && isJHHTokenRequired === true) {
            notify(fallbackErrorMessage); //todo handle server error
        } else if (message === "Malformed UTF-8 data") {
            let _hasBridgeHelper = await apiErrorIntentHanlder({
                value: "Malformed UTF-8 data",
            });
            if (!_hasBridgeHelper) forceLogout();
        } else {
            throw err;
        }
    }
};
